import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import NotFound from "@/pages/NotFound";
import { FaqItem } from "@/pages/ServiceDetail";
import { LOCAL_PAGES, localBySlug } from "@/data/local";
import { serviceBySlug } from "@/data/services";
import { BUSINESS, breadcrumbSchema, faqSchema, serviceSchema } from "@/lib/seo";

const Mono = ({ children, className = "" }) => <p className={`font-mono-tech text-[10px] uppercase tracking-[0.3em] ${className}`}>{children}</p>;

export default function LocalPage() {
  const slug = useLocation().pathname.replace(/^\/+|\/+$/g, "");
  const page = localBySlug(slug);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!page) return;
    api.get("/products").then((r) => {
      const bySlug = Object.fromEntries(r.data.map((p) => [p.slug, p]));
      setProducts(page.products.map((s) => bySlug[s]).filter(Boolean));
    }).catch(() => {});
  }, [page]);

  if (!page) return <NotFound />;

  const path = `/${page.slug}`;
  const service = serviceBySlug(page.service);
  const related = page.related.map(localBySlug).filter(Boolean);
  const areaServed = [{ "@type": page.areaType, name: page.area === "Alberta" ? "Alberta" : `${page.area}, Alberta` }];

  return (
    <main data-testid={`local-page-${page.slug}`} className="pt-40 pb-32 min-h-screen">
      <Seo
        title={page.metaTitle}
        description={page.metaDescription}
        path={path}
        image={products[0]?.image}
        jsonLd={[
          serviceSchema({ name: page.h1, description: page.metaDescription, path, serviceType: page.serviceType, areaServed }),
          breadcrumbSchema([["Home", "/"], ["Services", "/services"], [page.h1, path]]),
          faqSchema(page.faqs),
        ]}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <nav aria-label="Breadcrumb" className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex gap-3" data-testid="local-breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link><span>/</span>
          <Link to="/services" className="hover:text-white">Services</Link><span>/</span>
          <span className="text-zinc-300">{page.area}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-10">
          <div className="lg:col-span-7">
            <Reveal>
              <Mono className="text-[#C87455]">{page.eyebrow}</Mono>
              <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight leading-tight">{page.h1}</h1>
              <p className="text-zinc-300 text-base md:text-lg mt-8 leading-relaxed">{page.intro}</p>
              <div className="flex flex-wrap gap-4 mt-10">
                <Link to={page.cta.to} data-testid="local-primary-cta" className="bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm inline-flex items-center gap-3">{page.cta.label} <ArrowRight size={14} /></Link>
                {service && <Link to={`/services/${service.slug}`} data-testid="local-service-link" className="border border-[#A9B0B7]/50 text-[#A9B0B7] hover:bg-[#A9B0B7] hover:text-black transition-colors px-8 py-4 font-mono-tech uppercase tracking-[0.2em] text-sm">Full {service.nav.toLowerCase()} details</Link>}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <dl className="border border-zinc-900 bg-[#0A0A0A] divide-y divide-zinc-900" data-testid="local-facts">
                {page.facts.map(([k, v]) => (
                  <div key={k} className="p-5 grid grid-cols-3 gap-4">
                    <dt className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600 pt-0.5">{k}</dt>
                    <dd className="col-span-2 text-sm text-zinc-300">{v}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </div>

        <Reveal>
          <section className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-10" data-testid="local-sections">
            {page.sections.map(([heading, body]) => (
              <div key={heading}>
                <h2 className="font-serif-display text-2xl md:text-3xl text-white">{heading}</h2>
                <p className="text-sm text-zinc-400 leading-relaxed mt-4">{body}</p>
              </div>
            ))}
          </section>
        </Reveal>

        {products.length > 0 && (
          <section className="mt-24" data-testid="local-products">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-10">
                <h2 className="font-serif-display text-3xl md:text-4xl text-white">Ready-to-personalise pieces</h2>
                <Link to="/shop" className="link-line hidden sm:block font-mono-tech text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white">All products →</Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </section>
        )}

        <section className="mt-24 max-w-4xl" data-testid="local-faqs">
          <Reveal>
            <Mono className="text-[#C87455]">Questions from {page.area}</Mono>
            <h2 className="font-serif-display text-3xl md:text-4xl text-white mt-3">Frequently asked</h2>
          </Reveal>
          <div className="mt-8 border-t border-zinc-900">{page.faqs.map(([q, a], i) => <FaqItem key={q} q={q} a={a} idx={i} />)}</div>
        </section>

        <section className="mt-24" data-testid="local-related">
          <Mono className="text-zinc-500 mb-6">Other service areas</Mono>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {related.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} data-testid={`local-related-${r.slug}`} className="bg-[#080808] hover:bg-[#0C0C0C] transition-colors p-6 block">
                <h3 className="font-serif-display text-xl text-white">{r.h1}</h3>
                <p className="text-zinc-500 text-xs mt-2 line-clamp-2">{r.metaDescription}</p>
              </Link>
            ))}
          </div>
          <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-6">
            {BUSINESS.name} · {BUSINESS.locality}, Alberta · <a href={`tel:${BUSINESS.phone}`} className="hover:text-[#C87455]">{BUSINESS.phoneDisplay}</a> · {LOCAL_PAGES.map((p, i) => <span key={p.slug}><Link to={`/${p.slug}`} className="hover:text-[#C87455]">{p.nav}</Link>{i < LOCAL_PAGES.length - 1 ? " · " : ""}</span>)}
          </p>
        </section>
      </div>
    </main>
  );
}
